import { forwardRef } from 'react';
import { useReducedMotion } from 'motion/react';
import { cn } from '@/lib/cn';
import { habitColor } from '@/lib/habitColor';
import { clamp01, ringCircumference, ringDashOffset } from '@/lib/ringGeometry';

export type HabitRingState = 'todo' | 'partial' | 'done' | 'skipped';

export interface HabitRingProps {
  state: HabitRingState;
  /** 0–1, used only when state === 'partial' */
  progress?: number;
  /** Rendered pixel size; viewBox is fixed at 22 so SVG scales perfectly. */
  size?: number;
  /** Emoji or single glyph rendered at the ring center. */
  icon?: string;
  /** Accessible label. If omitted, the SVG is treated as decorative. */
  ariaLabel?: string;
  /** Optional override for the foreground stroke when state is 'done' or 'partial'. Falls back to semantic tokens. */
  color?: string;
  className?: string;
}

const RADIUS = 8.5;
const CIRCUMFERENCE = ringCircumference(RADIUS);

function strokeFor(state: HabitRingState): string {
  switch (state) {
    case 'done':
      return 'var(--ok)';
    case 'partial':
      return 'var(--info)';
    default:
      return 'var(--line-2)';
  }
}

export const HabitRing = forwardRef<SVGSVGElement, HabitRingProps>(function HabitRing(
  { state, progress = 0, size = 22, icon, ariaLabel, color, className },
  ref,
) {
  const reduced = useReducedMotion();
  const isSkipped = state === 'skipped';
  const showForeground = state === 'done' || state === 'partial';
  const dashOffset =
    state === 'done'
      ? 0
      : state === 'partial'
        ? ringDashOffset(RADIUS, clamp01(progress))
        : CIRCUMFERENCE;
  // habitColor() maps legacy pre-v4 hexes onto the governed palette vars.
  const foregroundStroke = color && showForeground ? habitColor(color) : strokeFor(state);
  const iconFontSize = (size / 22) * 9;

  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 22 22"
      className={cn('block', className)}
      role="img"
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      data-state={state}
    >
      <circle
        cx="11"
        cy="11"
        r="8.5"
        fill="none"
        stroke={isSkipped ? 'var(--line-2)' : foregroundStroke}
        strokeWidth="2.4"
        strokeDasharray={isSkipped ? '3 3' : undefined}
        opacity={isSkipped ? 1 : showForeground ? 0.18 : 1}
      />
      {showForeground && (
        <circle
          cx="11"
          cy="11"
          r="8.5"
          fill="none"
          stroke={foregroundStroke}
          strokeWidth="2.4"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform="rotate(-90 11 11)"
          style={{
            transition: reduced ? 'none' : 'stroke-dashoffset var(--dur-modal) var(--ease-out)',
          }}
        />
      )}
      {icon && (
        <text x="11" y="14" textAnchor="middle" fontSize={iconFontSize} aria-hidden="true">
          {icon}
        </text>
      )}
    </svg>
  );
});
